import { Component, Input, OnChanges, OnInit, Output, EventEmitter, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { WebService, lastValueFrom } from '@services';
import { RmsModule } from '@rms';

interface AvailableOffering {
  InventoryID: number;
  InventoryTypeID: number;
  InventoryTypeName: string;
  AvailableQty: number;
  ForwardQty: number;
  LatestInventoryTypePriceJson: string | null;
  FinalDestinationWarehouseID: number | null;
  WarehouseName: string | null;
  EntryBags?: number | null;
  EntryPrice?: number | null;
  ReserveOrderDetailID?: number;
  RemainingReservedBags?: number;
  PhysicalAvailableQty?: number;
  WarehouseID?: number;
  NetWeight?: number;
  SlackBag?: boolean | number;
  Organic?: boolean | number;
  Decaf?: boolean | number;
  DecaffeinatorID?: number;
}

// One row per SKU (InventoryTypeID), each holding its individual
// reference-number offerings underneath — matches royalny.com/coffee/offerings'
// "SKU header + Show All Offerings (N)" pattern.
interface SkuGroup {
  InventoryTypeID: number;
  InventoryTypeName: string;
  TotalAvailableQty: number;
  Offerings: AvailableOffering[];
  Expanded: boolean;
}

@Component({
  selector: 'app-customer-reserves-orders',
  imports: [RmsModule, FormsModule],
  templateUrl: './customer-reserves-orders.component.html',
  styleUrl: './customer-reserves-orders.component.scss'
})
export class CustomerReservesOrdersComponent implements OnInit, OnChanges {
  @Input() CompanyID:number|null=null;
  @Input() OrderID:number|null=null;
  ReserveOfferings:AvailableOffering[]=[];
  @Input() Warehouses: any[] = [];
  @Input() WarehouseNumber: number | null = null;
  @Input() LineItems: any[] = [];

  @Output() onWarehouseChanged = new EventEmitter<number | null>();
  @Output() onLineItemsChanged = new EventEmitter<any[]>();

  SkuGroups: SkuGroup[] = [];
  Loading = false;
  LoadError = '';
  SearchQuery = '';
  OfferingFilter: 'all' | 'reserved' | 'spot' = 'all';
  ReservesExpanded = false;
  CartMessage = '';
  CartError = '';

  get matchingReferenceCount(): number {
    return (this.OfferingFilter !== 'spot' ? this.visibleReserves.length : 0)
      + (this.OfferingFilter !== 'reserved' ? this.filteredSkuGroups.reduce((sum,g)=>sum+g.Offerings.length,0) : 0);
  }
  get hasWarehouseOfferings(): boolean {
    return this.SkuGroups.length > 0 || this.warehouseReserves.length > 0;
  }
  get warehouseReserves(): AvailableOffering[] {
    return this.ReserveOfferings.filter(row=>row.AvailableQty>0 &&
      (row.WarehouseID===this.WarehouseNumber || this.WarehouseNumber===37 && row.WarehouseID===43));
  }
  get reservesOpen(): boolean { return this.ReservesExpanded || !!this.SearchQuery.trim() || this.OfferingFilter==='reserved'; }
  selectOnFocus(event: FocusEvent) { (event.target as HTMLInputElement).select(); }
  unitName(row: {NetWeight?: number}, plural = true): string {
    return Number(row.NetWeight)===22 ? (plural ? 'boxes' : 'box') : (plural ? 'bags' : 'bag');
  }
  inCart(offering: AvailableOffering): number {
    return this.LineItems.filter(item=>item.InventoryID===offering.InventoryID).reduce((sum,item)=>sum+Number(item.NumberOfBags || 0),0);
  }
  entryIssue(offering: AvailableOffering): string {
    const rule=this.restriction(offering);
    if(rule) return rule;
    if(this.remainingAvailable(offering)===0) return 'All available units are in your order.';
    if(offering.EntryBags!=null && (!Number.isInteger(offering.EntryBags) || offering.EntryBags<=0)) return 'Enter a positive whole quantity.';
    if(Number(offering.EntryBags)>this.remainingAvailable(offering)) return 'Only '+this.remainingAvailable(offering)+' '+this.unitName(offering)+' remain available.';
    if(offering.EntryPrice!=null && (!Number.isFinite(offering.EntryPrice) || offering.EntryPrice<=0)) return 'Enter a price greater than zero.';
    return '';
  }
  lineTotal(item: any): number | null {
    return Number.isFinite(item.NetWeight) && item.NetWeight>0 ? item.NumberOfBags*item.NetWeight*item.Price : null;
  }
  private sourceForItem(item: any): AvailableOffering | undefined {
    return item.ReserveOrderDetailID
      ? this.ReserveOfferings.find(row=>row.ReserveOrderDetailID===item.ReserveOrderDetailID)
      : this.SkuGroups.flatMap(group=>group.Offerings).find(row=>row.InventoryID===item.InventoryID);
  }
  cartLimit(item: any): number {
    const source=this.sourceForItem(item);
    return source ? this.remainingAvailable(source)+Number(item.NumberOfBags) : Number(item.NumberOfBags);
  }
  updateQuantity(item: any, value: number | null): boolean {
    this.CartError=''; this.CartMessage='';
    if(this.Loading || this.LoadError) { this.CartError='Wait for inventory to load before changing quantities.'; return false; }
    if(!Number.isInteger(value) || Number(value)<=0 || Number(value)>this.cartLimit(item)) {
      this.CartError='Ref #'+item.InventoryID+': enter a whole quantity from 1 to '+this.cartLimit(item)+'.'; return false;
    }
    const source=this.sourceForItem(item);
    if(!source || this.restriction(source)) { this.CartError='This reference is no longer available to add. Review the selected warehouse and reserves.'; return false; }
    const later=Number(value)<item.NumberOfBags ? this.laterReserveIds(item) : [];
    const updated=this.LineItems.filter(row=>!later.includes(row.ReserveOrderDetailID)).map(row=>row.InventoryID===item.InventoryID ? {...row,NumberOfBags:Number(value)} : row);
    if(updated.length<this.LineItems.length) this.CartMessage='Later reserve references were removed so the oldest reserve is used first.';
    this.onLineItemsChanged.emit(updated); return true;
  }
  changeCartQuantity(item: any, event: Event) {
    const input=event.target as HTMLInputElement;
    if(!this.updateQuantity(item,input.valueAsNumber)) input.value=String(item.NumberOfBags);
  }
  private laterReserveIds(item: any): (number | undefined)[] {
    if(!item?.ReserveOrderDetailID) return [];
    const source=this.ReserveOfferings.find(row=>row.ReserveOrderDetailID===item.ReserveOrderDetailID);
    if(!source) return [];
    const sku=this.ReserveOfferings.filter(row=>row.InventoryTypeID===source.InventoryTypeID);
    return sku.slice(sku.indexOf(source)+1).map(row=>row.ReserveOrderDetailID);
  }
  private offeringsRequest = 0;
  private collapsedCategories = new Set<string>(['Conventional Full Bag','Organic Full Bag','Robusta Full Bag','Decaf Full Bag','Royal Line Up','Slack Bags']);
  get offeringCategories() {
    const categories=new Map<string,SkuGroup[]>();
    for(const group of this.filteredSkuGroups) {
      const packages=new Map<string,AvailableOffering[]>();
      for(const row of group.Offerings) {
        const name=group.InventoryTypeName;
        const category=Number(row.NetWeight)===22 ? 'Royal Line Up'
          : row.SlackBag===true || Number(row.SlackBag)===1 ? 'Slack Bags'
          : row.Decaf || row.DecaffeinatorID || /\bdecaf(?:feinated)?\b/i.test(name) ? 'Decaf Full Bag'
          : /\brobusta\b/i.test(name) ? 'Robusta Full Bag'
          : row.Organic || /\borganic\b/i.test(name) ? 'Organic Full Bag' : 'Conventional Full Bag';
        packages.set(category,[...(packages.get(category) ?? []),row]);
      }
      for(const [category,offerings] of packages) {
        categories.set(category,[...(categories.get(category) ?? []),{
          ...group, Offerings:offerings, TotalAvailableQty:offerings.reduce((sum,row)=>sum+Number(row.AvailableQty || 0),0)
        }]);
      }
    }
    return ['Conventional Full Bag','Organic Full Bag','Robusta Full Bag','Decaf Full Bag','Royal Line Up','Slack Bags'].filter(name=>categories.has(name)).map(name=>({
      name, groups:categories.get(name)!, expanded:!!this.SearchQuery.trim() || !this.collapsedCategories.has(name)
    }));
  }
  toggleCategory(name:string) { if(this.collapsedCategories.has(name)) this.collapsedCategories.delete(name); else this.collapsedCategories.add(name); }
  productName(item:any):string {
    const reserved=item.ReserveOrderDetailID ? this.ReserveOfferings.find(row=>row.ReserveOrderDetailID===item.ReserveOrderDetailID) : undefined;
    const offering=reserved ?? this.SkuGroups.flatMap(group=>group.Offerings).find(row=>row.InventoryID===item.InventoryID);
    return item.InventoryTypeName?.trim() || offering?.InventoryTypeName || item.CoffeeName || item.Description || 'Coffee · Ref #'+item.InventoryID;
  }

  get TotalBags(): number {
    return this.LineItems.reduce((sum, i) => sum + (i.NumberOfBags ?? 0), 0);
  }

  get filteredSkuGroups(): SkuGroup[] {
    const query = this.SearchQuery.trim().toLowerCase();
    if (!query) return this.SkuGroups;
    return this.SkuGroups.map(group => ({
      ...group,
      Expanded: true,
      Offerings: group.Offerings.filter(o => `${o.InventoryID} ${o.InventoryTypeID} ${o.InventoryTypeName} ${o.WarehouseName ?? ''}`.toLowerCase().includes(query))
    })).filter(group => group.Offerings.length > 0);
  }

  constructor(
    private webService: WebService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    if (this.WarehouseNumber) {
      await this.loadAvailableOfferings();
    }
  }

  // Single source of truth for triggering the load: whenever the parent's
  // [WarehouseNumber] input actually changes (whether because the user picked
  // a warehouse here, or because an existing order was loaded with one already
  // set), this fires exactly once and reloads. See onWarehouseSelected() below
  // for why loading is NOT also triggered from there.
  async ngOnChanges(changes: SimpleChanges) {
    if (['WarehouseNumber','CompanyID','OrderID'].some(key=>changes[key] && !changes[key].firstChange)) {
      await this.loadAvailableOfferings();
    }
  }

  // FIXED: this used to also call loadAvailableOfferings() directly, in
  // addition to emitting the change. That caused a race: emitting updates the
  // parent's WarehouseNumber, which re-binds this component's own
  // [WarehouseNumber] @Input, which triggers ngOnChanges() above — which ALSO
  // called loadAvailableOfferings(). Two (or more, across repeated change
  // detection cycles) requests fired for a single warehouse pick, and
  // whichever one resolved last silently won, overwriting SkuGroups —
  // sometimes with a stale/empty result even though real data had already
  // come back from an earlier call. This is why the "no available inventory"
  // message showed up despite the network tab clearly showing real rows.
  // Fix: this method ONLY emits now. ngOnChanges is the one and only place
  // that ever calls loadAvailableOfferings().
  async onWarehouseSelected(warehouseNumber: number) {
    this.onWarehouseChanged.emit(warehouseNumber);
  }

  // No longer takes a warehouseNumber parameter — always reads this.WarehouseNumber
  // directly, since that @Input is now the single source of truth for which
  // warehouse to load, set exclusively via the parent's binding.
  async loadAvailableOfferings() {
    const request = ++this.offeringsRequest;
    const warehouse = this.WarehouseNumber;
    this.SkuGroups = [];
    this.ReserveOfferings=[];
    this.LoadError = '';
    if (!this.WarehouseNumber) {
      this.Loading = false;
      return;
    }

    this.Loading = true;
    try {
      const [result,reserves]:[AvailableOffering[],any[]] = await Promise.all([lastValueFrom(
        this.webService.getWebCall('inventory', 'coffee/available_inventory_details', {
          params: [{ name: 'WarehouseID', value: warehouse }, {name:'OrderID',value:this.OrderID ?? ''}]
        })
      ), this.CompanyID ? lastValueFrom(this.webService.getWebCall('orders', `coffee/customers/${this.CompanyID}/order-reserves`, {params:[{name:'OrderID',value:this.OrderID ?? ''}]})) : Promise.resolve([])]);
      if (request !== this.offeringsRequest || warehouse !== this.WarehouseNumber) return;

      const withEntryFields = (result ?? []).map(row => ({
        ...row,
        EntryBags: null,
        EntryPrice: this.extractLatestFixedPrice(row.LatestInventoryTypePriceJson)
      }));

      this.ReserveOfferings=reserves.map(row=>({...row,EntryBags:null,EntryPrice:Number(row.Price)}));
      this.SkuGroups = this.groupBySku(withEntryFields);
    } catch {
      if (request === this.offeringsRequest) {
        this.LoadError = 'Available inventory could not be loaded. Please try again.';
      }
    } finally {
      if (request === this.offeringsRequest) {
        this.Loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  // Groups the flat offerings list by InventoryTypeID (the "SKU"), matching
  // royalny.com's "one SKU, many reference numbers" layout. ASSUMPTION FLAGGED:
  // treats InventoryTypeID as the SKU — CoffeeSKUGroupID looks like a separate,
  // unrelated admin/pricing grouping concept based on how it's used in
  // coffee-grouping.component.ts, not this one. Still pending confirmation
  // from your manager.
  private groupBySku(offerings: AvailableOffering[]): SkuGroup[] {
    const groups = new Map<number, SkuGroup>();

    for (const o of offerings) {
      if (!groups.has(o.InventoryTypeID)) {
        groups.set(o.InventoryTypeID, {
          InventoryTypeID: o.InventoryTypeID,
          InventoryTypeName: o.InventoryTypeName ?? `(Unnamed SKU ${o.InventoryTypeID})`,
          TotalAvailableQty: 0,
          Offerings: [],
          Expanded: false
        });
      }
      const group = groups.get(o.InventoryTypeID)!;
      group.TotalAvailableQty += Number(o.AvailableQty || 0);
      group.Offerings.push(o);
    }

    return Array.from(groups.values()).sort((a, b) => a.InventoryTypeName.localeCompare(b.InventoryTypeName));
  }

  toggleGroup(group: SkuGroup) {
    const original=this.SkuGroups.find(item=>item.InventoryTypeID===group.InventoryTypeID);
    if(original) original.Expanded=!original.Expanded;
  }

  private extractLatestFixedPrice(json: string | null): number | null {
    if (!json) return null;
    try {
      const parsed = JSON.parse(json);
      return parsed?.AskPriceFixed ? Number(parsed.AskPriceFixed) : null;
    } catch {
      return null;
    }
  }

  get visibleReserves() {
    const query=this.SearchQuery.trim().toLowerCase();
    return this.warehouseReserves.filter(row=>!query || (row.InventoryTypeName+' '+row.InventoryID+' '+row.InventoryTypeID+' '+(row.WarehouseName ?? '')).toLowerCase().includes(query));
  }
  restriction(offering:AvailableOffering):string {
    const reserves=this.ReserveOfferings.filter(row=>row.InventoryTypeID===offering.InventoryTypeID);
    if(!offering.ReserveOrderDetailID) return reserves.length ? 'This coffee is on reserve. Order from Reserved coffee.' : '';
    for(const row of reserves) {
      if(row.ReserveOrderDetailID===offering.ReserveOrderDetailID) break;
      const selected=this.LineItems.filter(item=>item.ReserveOrderDetailID===row.ReserveOrderDetailID).reduce((sum,item)=>sum+item.NumberOfBags,0);
      if(selected < (row.RemainingReservedBags ?? 0)) return 'Use the oldest reserve first: Ref #'+row.InventoryID;
    }
    return '';
  }
  remainingAvailable(offering:AvailableOffering):number {
    const inCart=this.LineItems.filter(item=>item.InventoryID===offering.InventoryID).reduce((sum,item)=>sum+Number(item.NumberOfBags || 0),0);
    return Math.max(0,Number(offering.AvailableQty || 0)-inCart);
  }
  canAddToOrder(offering: AvailableOffering): boolean {
    const existing=this.LineItems.find(item=>item.InventoryID===offering.InventoryID);
    return (!existing || (existing.ReserveOrderDetailID ?? null)===(offering.ReserveOrderDetailID ?? null)) && !this.restriction(offering) && Number(offering.EntryBags)<=this.remainingAvailable(offering) && !this.Loading && !this.LoadError && Number.isInteger(offering.EntryBags)
      && Number(offering.EntryBags) > 0 && Number.isFinite(offering.EntryPrice)
      && Number(offering.EntryPrice) > 0;
  }

  addToOrder(offering: AvailableOffering) {
    if (!this.canAddToOrder(offering)) return;

    const existing = this.LineItems.find(i => i.InventoryID === offering.InventoryID);
    const updated = existing
      ? this.LineItems.map(i => i.InventoryID === offering.InventoryID
        ? { ...i, InventoryTypeName: offering.InventoryTypeName, NumberOfBags: i.NumberOfBags + offering.EntryBags!, Price: offering.EntryPrice }
        : i)
      : [...this.LineItems, {
        InventoryID: offering.InventoryID,
        ReserveOrderDetailID:offering.ReserveOrderDetailID ?? null,
        InventoryTypeID: offering.InventoryTypeID,
        InventoryTypeName: offering.InventoryTypeName,
        Price: offering.EntryPrice,
        NumberOfBags: offering.EntryBags,
        NetWeight: offering.NetWeight,
        Validated: null
      }];

    this.onLineItemsChanged.emit(updated);
    offering.EntryBags = null;
  }

  removeFromOrder(inventoryId: number) {
    const removed=this.LineItems.find(i=>i.InventoryID===inventoryId);
    const later=this.laterReserveIds(removed);
    const updated=this.LineItems.filter(i=>i.InventoryID!==inventoryId && !later.includes(i.ReserveOrderDetailID));
    this.CartError='';
    this.CartMessage=this.LineItems.length-updated.length>1
      ? 'This reference and its later reserves were removed to preserve oldest-first ordering.' : 'Coffee removed from your order.';
    this.onLineItemsChanged.emit(updated);
  }
}
